// Card row order (founder 2026-10-02, quest card-order). Factories: Ethiopian
// cards first, then Kenyan ones, everything else last (orderCardsByCountry).
// Farmers: a fixed list of cohorts (orderFarmerCards; founder 2026-10-03
// replaced the country grouping on this row). Forests keeps the order the
// layer serves.

export type CardCountry = 'ethiopia' | 'kenya';

// DEPENDS ON UPSTREAM WORDING. Neither factory_group_summary nor
// farmer_cards_public serves a country field (checked 2026-10-02), so the
// country is read from the card title, which Fairchain writes by hand and can
// change with no notice (CLAUDE.md S4). It already has once: the Nairobi
// roastery was served as "Roastery in Nairobi, Kenia" on 2026-08-31 (the e2e
// fixture) and as "Roastery in Nairobi, Kenya" on 2026-10-02, so both
// spellings are accepted.
// This is the ONLY place a title is turned into a country; if the wording
// changes, fix it here. A title that matches nothing is not guessed at: the
// card simply sorts last, in its served position.
const COUNTRY_PATTERNS: { country: CardCountry; pattern: RegExp }[] = [
  { country: 'ethiopia', pattern: /\bethiopi(a|ë|e)\b/i },
  { country: 'kenya', pattern: /\bken(y|i)a\b/i },
];

// Display order; a country not listed here (or none found) ranks after these.
const COUNTRY_RANK: Record<CardCountry, number> = { ethiopia: 0, kenya: 1 };
const UNKNOWN_RANK = 2;

/**
 * The country a card title names, or null when it names none of the known
 * countries or more than one of them (ambiguous, so not guessed).
 */
export const countryFromCardTitle = (title: string | null | undefined): CardCountry | null => {
  const matches = COUNTRY_PATTERNS.filter(({ pattern }) => pattern.test(title ?? ''));
  return matches.length === 1 ? matches[0].country : null;
};

/**
 * Ethiopian cards, then Kenyan, then the rest. The incoming order is kept
 * within each of the three groups. Returns a new array.
 */
export const orderCardsByCountry = <T extends { title?: string | null }>(cards: T[]): T[] => {
  const rank = (card: T) => {
    const country = countryFromCardTitle(card.title);
    return country ? COUNTRY_RANK[country] : UNKNOWN_RANK;
  };
  return cards
    .map((card, index) => ({ card, index, rank: rank(card) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ card }) => card);
};

// Farmers row, founder 2026-10-03: Limu, Mizan, Kericho, Mt Elgon, then every
// other card in its current order. DEPENDS ON UPSTREAM WORDING exactly like
// COUNTRY_PATTERNS above: farmer_cards_public titles as served 2026-10-03 are
// "Farmers in Limu, Ethiopia", "Farmers in Mizan, Ethiopia", "Farmers in
// Kericho, Kenya", "Farmers in Mt Elgon, Uganda" and "Farmers in Bench Maji,
// Ethiopia" (not in the list, so it follows the four). A title that matches
// no slot, or more than one, is not guessed at and joins the remaining cards.
const FARMER_CARD_SLOTS: RegExp[] = [
  /\blimu\b/i,
  /\bmizan\b/i,
  /\bkericho\b/i,
  /\b(mt\.?|mount) elgon\b/i,
];

/** Position of a farmer card title in FARMER_CARD_SLOTS, or null. */
export const farmerCardSlot = (title: string | null | undefined): number | null => {
  const hits = FARMER_CARD_SLOTS.flatMap((pattern, slot) =>
    pattern.test(title ?? '') ? [slot] : [],
  );
  return hits.length === 1 ? hits[0] : null;
};

/**
 * Listed cohorts in list order, then the rest. The incoming order is kept
 * within a slot (if two cards ever match one) and among the rest. Returns a
 * new array.
 */
export const orderFarmerCards = <T extends { title?: string | null }>(cards: T[]): T[] => {
  const rank = (card: T) => farmerCardSlot(card.title) ?? FARMER_CARD_SLOTS.length;
  return cards
    .map((card, index) => ({ card, index, rank: rank(card) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ card }) => card);
};
